// The companies_summary derived fields (spec 02 §6), computed in TS for FakeRest/demo mode.
// SQL twin: public.companies_summary (21_gavion_leads_views.sql).
import { queueOrderKey, type QueueContext, type QueueLead } from "./leadQueue";

export const CLOSED_DEAL_STAGES: readonly string[] = Object.freeze([
  "gagne",
  "perdu",
]);

type Row = Record<string, unknown> & { id: number | string };
export type SummaryInputs = {
  sales: {
    id: number | string;
    first_name?: string | null;
    last_name?: string | null;
  }[];
  deals: {
    company_id: number | string | null;
    stage: string;
    archived_at?: string | null;
  }[];
  tasks: {
    company_id?: number | string | null;
    due_date?: string | null;
    done_date?: string | null;
  }[];
  contacts: { company_id: number | string | null }[];
};

const LEAD_DEFAULTS: Readonly<Record<string, unknown>> = Object.freeze({
  trade: null,
  segment: null,
  fit_score: null,
  high_quality: false,
  estimated_employees: null,
  phone_e164: null,
  phone_valid: true,
  source_id: null,
  source_query: null,
  date_added: null,
  assigned_sales_id: null,
  prospect_status: "new",
  last_call_outcome: null,
  last_called_at: null,
  next_callback_at: null,
  attempt_count: 0,
  cooldown_until: null,
  locked_by: null,
  locked_until: null,
  language: "fr",
  email: null,
  region: null,
  timezone: "America/Toronto",
  import_batch_id: null,
});

/** A copy of `raw` with every prospecting column present (upstream forms do not send them). */
export function withLeadDefaults(raw: Row): Row {
  const out: Record<string, unknown> = { ...LEAD_DEFAULTS, tags: [] };
  for (const [key, value] of Object.entries(raw))
    if (value !== undefined) out[key] = value;
  return out as Row;
}

export function summarizeLead(
  raw: Row,
  inputs: SummaryInputs,
  ctx: QueueContext,
): Row {
  const lead = withLeadDefaults(raw);
  const id = String(lead.id);
  const phone = lead.phone_e164 as string | null;
  const lockedUntil = lead.locked_until as string | null;
  const rep = inputs.sales.find(
    (s) => String(s.id) === String(lead.assigned_sales_id),
  );
  const deals = inputs.deals.filter((d) => String(d.company_id) === id);
  const openTasks = inputs.tasks
    .filter((t) => String(t.company_id) === id && !t.done_date && t.due_date)
    .map((t) => t.due_date as string)
    .sort();
  const isPhoneSuppressed = phone !== null && ctx.suppressedPhones.has(phone);
  const queueOrder = queueOrderKey(
    { ...(lead as unknown as QueueLead), id: Number(lead.id) },
    ctx,
  );
  return {
    ...lead,
    nb_deals: deals.length,
    nb_contacts: inputs.contacts.filter((c) => String(c.company_id) === id)
      .length,
    assigned_rep_name: rep
      ? [rep.first_name, rep.last_name].filter(Boolean).join(" ")
      : null,
    open_deal_count: deals.filter(
      (d) => !d.archived_at && !CLOSED_DEAL_STAGES.includes(d.stage),
    ).length,
    last_email_at: null,
    next_task_due: openTasks[0] ?? null,
    is_phone_suppressed: isPhoneSuppressed,
    is_locked:
      lockedUntil !== null && Date.parse(lockedUntil) > ctx.now.getTime(),
    is_dnc: lead.prospect_status === "do_not_call" || isPhoneSuppressed,
    has_email: lead.email !== null && lead.email !== "",
    is_assigned: lead.assigned_sales_id !== null,
    has_callback: lead.next_callback_at !== null,
    // GAVION test mode: a test lead is never in « À appeler aujourd'hui » (SQL companies_summary, 58_)
    queue_eligible: queueOrder !== null && lead.is_test !== true,
    queue_order: queueOrder,
  };
}
